import { Hash, CheckSquare, HelpCircle, Terminal, Mic, Menu, LogOut, Users, Sparkles, Radio, UserCog } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { CHANNEL_META } from "@/lib/constants";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import { EmergentButton } from "@/components/EmergentPresence";
import EditProfile from "@/components/EditProfile";

const ICONS = { Hash, CheckSquare, HelpCircle, Terminal, Mic, Radio };

export default function Header({ channel, onlineCount, onOpenVoice, onOpenSidebar, channels, onNotificationNavigate, emergentActive, meEmergent, myEmergentStart, onToggleEmergent }) {
  const { user, logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const Icon = ICONS[CHANNEL_META[channel?.id]?.icon] || Hash;

  return (
    <header className="h-16 shrink-0 border-b border-[#2E3242] bg-[#16181F]/80 backdrop-blur-md flex items-center gap-3 px-4 md:px-6">
      <button
        data-testid="open-sidebar-button"
        onClick={onOpenSidebar}
        className="md:hidden text-gray-400 hover:text-white"
      >
        <Menu className="w-5 h-5" />
      </button>

      <div className="flex items-center gap-2.5 min-w-0">
        <Icon className="w-5 h-5 text-red-400 shrink-0" />
        <div className="min-w-0">
          <h1 data-testid="active-channel-name" className="font-display font-bold text-base leading-tight truncate">
            #{channel?.name}
          </h1>
          <p className="text-xs text-gray-500 truncate hidden sm:block">{channel?.description}</p>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-4">
        <EmergentButton
          meActive={meEmergent}
          activeCount={emergentActive?.length || 0}
          startedAt={myEmergentStart}
          onToggle={onToggleEmergent}
        />

        <div className="hidden md:flex items-center gap-1.5 text-xs text-gray-400 px-2.5 py-1.5 rounded-full bg-[#1E2029] border border-[#2E3242]">
          <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-glow" />
          <Users className="w-3.5 h-3.5" />
          <span data-testid="online-count">{onlineCount} online</span>
        </div>

        <NotificationBell channels={channels} onNavigate={onNotificationNavigate} />

        <Button
          data-testid="header-voice-button"
          onClick={onOpenVoice}
          className="ruby-gradient text-white font-semibold border border-red-900/40 hover:opacity-90 h-9"
        >
          <Sparkles className="w-4 h-4 sm:mr-1.5" />
          <span className="hidden sm:inline">AI Assistant</span>
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="user-menu-button" className="flex items-center gap-2">
              <img
                src={user?.avatar}
                alt={user?.name}
                className="w-9 h-9 rounded-full object-cover border-2 border-red-900/50 hover:border-red-600 transition-colors"
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-56 bg-slate-900/95 backdrop-blur-md border-red-900/30 text-gray-200"
          >
            <div className="flex items-center gap-3 pb-3 border-b border-[#2E3242]">
              <img src={user?.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              data-testid="edit-profile-button"
              onClick={() => setProfileOpen(true)}
              className="mt-2 w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-gray-300 hover:bg-[#262936] hover:text-white transition-colors"
            >
              <UserCog className="w-4 h-4" /> Edit profile
            </button>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="mt-1 w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-gray-300 hover:bg-red-950/50 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </PopoverContent>
        </Popover>
      </div>

      <EditProfile open={profileOpen} onOpenChange={setProfileOpen} />
    </header>
  );
}
